import React from 'react';
import { Transition } from 'react-transition-group';

import MegaMenu from 'components/layout/MegaMenu/MegaMenu';
import MegaMenuContent from 'components/layout/MegaMenu/MegaMenuContent';
import { Button } from 'components/ui';
import { useLayoutStore } from 'context/LayoutContext';
import { checkIfMobile } from 'helpers/BrowserHelpers';
import { cx } from 'helpers/StylingHelpers';
import { useOnClickOutside } from 'hooks/common/useOnClickOutside';
import { useTranslation } from 'hooks/common/useTranslation';
import { useAnalytics } from 'hooks/useAnalytics';
import { useOnScrollDown } from 'hooks/useOnScrollDown';

import MenuIcon from '../../MenuIcon';

import type { IMegaMenuController, IMenuItemLink } from 'types';

import styles from './MegaMenuDesktop.module.scss';

// Types

interface Props {
  isOpen: boolean,
  open?: () => void,
  close?: () => void,
  toggle?: () => void
}

interface MegaMenuTriggerProps {
  isMenuOpen: boolean,
  open?: () => void,
  toggle?: () => void
}

const MegaMenuDesktop = (props: Props) => {
  const {
    isOpen,
    open = () => {},
    close = () => {},
    toggle = () => {}
  } = props;

  // Refs

  const megaMenuRef = React.useRef<HTMLDivElement>(null);
  const megaMenuControllerRef = React.useRef<IMegaMenuController | null>(null);

  const transitionRef = React.useRef(null);

  const rootRef = useOnClickOutside(() => {
    close();
  });

  // Hooks

  const analytics = useAnalytics();

  const [menuHeight, setMenuHeight] = React.useState(0);
  const [maxMenuHeight, setMaxMenuHeight] = React.useState<number | 'inherit'>(0);

  const [selectedItem, setSelectedItem] = React.useState<IMenuItemLink>();

  const { isHomepageSubMenuVisible } = useLayoutStore();

  useOnScrollDown(() => {
    close();
  }, {
    distance: 50,
    enabled: isOpen
  });

  // Layout Effects

  React.useLayoutEffect(() => {
    if (isOpen && megaMenuRef.current) {
      setMenuHeight(megaMenuRef?.current ? megaMenuRef.current.clientHeight + 1 : 0);
      setMaxMenuHeight(getMaxMenuHeight());
    }
  }, [isOpen]);

  // Effects

  React.useEffect(() => {
    if (isOpen) {
      analytics.expandCategoryMenu();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Render

  return (
    <div
      ref={rootRef}
      className={cx(
        styles.root,
        { [styles.withBorder]: isHomepageSubMenuVisible }
      )}
      onMouseLeave={() => {
        if (checkIfMobile()) return;
        close();
      }}
    >

      {/* Button */}
      <MegaMenuTrigger
        isMenuOpen={isOpen}
        open={open}
        toggle={toggle}
      />

      {/* Menu */}
      <Transition
        nodeRef={transitionRef}
        in={isOpen}
        timeout={400}
        onExited={() => {
          megaMenuControllerRef?.current?.clearSelectedItem();
          megaMenuControllerRef?.current?.resetScroll();
        }}
      >
        {
          (state) => {
            return (
              <div
                className={cx(
                  styles.popover,
                  { [styles[state]]: state }
                )}
              >

                <div
                  className={cx(styles.inner)}
                  style={{
                    height: `${menuHeight}px`,
                    maxHeight: `${maxMenuHeight}px`
                  }}
                >

                  {/* Mega Menu */}
                  <div
                    className={cx(
                      styles.megaMenuWrapper,
                      { [styles.expanded]: !!selectedItem?.children?.length },
                      { [styles[state]]: state }
                    )}
                  >
                    <MegaMenu
                      ref={megaMenuControllerRef}
                      menuRef={megaMenuRef}
                      className={cx(
                        styles.megaMenu,
                        { [styles[state]]: state }
                      )}
                      onSelectedItemChange={(item) => setSelectedItem(item)}
                    />
                  </div>

                  {/* Mega Menu Content */}
                  <MegaMenuContent
                    className={styles.megaMenuContent}
                    selectedItem={selectedItem}
                  />

                </div>
              </div>
            );
          }
        }
      </Transition>
    </div>
  );
};

// Components

// --- Mega Menu Trigger

const MegaMenuTrigger = (props: MegaMenuTriggerProps) => {
  const {
    isMenuOpen,
    open = () => {},
    toggle = () => {}
  } = props;

  // Hooks

  const { t } = useTranslation();

  const { isHomepageMenuVisible } = useLayoutStore();

  // Props

  const isActive = isMenuOpen || isHomepageMenuVisible;
  const isEnabled = !isHomepageMenuVisible;

  // Render

  const isMobile = checkIfMobile();

  return (
    <div
      className={cx(
        styles.megaMenuTrigger,
        { [styles.active]: isActive }
      )}
    >
      <Button
        className={cx(
          styles.btn,
          'ignore-onclickoutside'
        )}
        variant="primary"
        size="medium"
        beforeContent={(
          <div className={styles.iconWrapper}>
            <MenuIcon
              className={styles.icon}
              open={isMobile && isActive ? isMenuOpen : false}
              color={isActive ? styles.colorPrimary : styles.colorWhite}
            />
          </div>
        )}
        onClick={() => {
          if (!isEnabled || !isMobile) return;
          toggle();
        }}
        onMouseEnter={() => {
          if (!isEnabled || isMobile) return;
          open();
        }}
      >
        {t('NAVIGATION.PRODUCTS')}
      </Button>
    </div>
  );
};

// Helpers

const getMaxMenuHeight = () => {
  try {
    const viewportHeight = window.innerHeight;

    const header = document.getElementById('header');
    const banner = document.getElementById('announcement-banner');

    if (!header) {
      throw new Error('Header or banner element not found.');
    }

    const headerHeight = header.clientHeight;

    let bannerHeight = 0;
    if (banner) {
      const bannerBoundingRect = banner.getBoundingClientRect();
      const bannerRelativeHeight = bannerBoundingRect.height + bannerBoundingRect.top;
      bannerHeight = bannerRelativeHeight < 0 ? 0 : bannerRelativeHeight;
    }

    const bufferHeight = 40;

    return viewportHeight - headerHeight - bannerHeight - bufferHeight + 1;
  } catch (e) {
    console.log('Failed to compute max menu height ', e);
    return 'inherit';
  }
};

// Export

export default MegaMenuDesktop;
